import type {PropertyValue} from '@github-ui/custom-properties-types'
import type {OnDropArgs} from '@github-ui/drag-and-drop'
import {DragAndDrop, useDragAndDrop} from '@github-ui/drag-and-drop'
import {GrabberIcon, KebabHorizontalIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, FormControl, IconButton, Label, TextInput} from '@primer/react'
import {clsx} from 'clsx'
import type {RefObject} from 'react'
import {useEffect, useRef} from 'react'

import {NEW_ALLOWED_VALUE_ELEMENT_ID} from '../../helpers/definition-form-validators'
import styles from './AllowedValuesEditor.module.css'

interface Props {
  allowedValues: string[]
  newAllowedValue: string
  newAllowedValueError?: string
  defaultValue?: PropertyValue
  onNewAllowedValueChange: (value: string) => void
  setNewAllowedValueRef?: (instance: HTMLInputElement | null) => void
  onAllowedValuesChange: (payload: {type: 'add' | 'remove' | 'reorder'; values: string[]}) => void
}

export function AllowedValuesEditor({
  allowedValues,
  newAllowedValue,
  newAllowedValueError,
  defaultValue: providedDefaultValues,
  onNewAllowedValueChange,
  onAllowedValuesChange,
  setNewAllowedValueRef,
}: Props) {
  const newAllowedValueRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setNewAllowedValueRef?.(newAllowedValueRef.current)
  }, [setNewAllowedValueRef])

  const allowedValuesItemsRefs = useRef<Array<React.RefObject<HTMLElement | null>>>([])

  const defaultValues = Array.isArray(providedDefaultValues) ? providedDefaultValues : [providedDefaultValues || '']

  const onDrop = ({dragMetadata, dropMetadata, isBefore}: OnDropArgs<string>) => {
    if (dragMetadata.id === dropMetadata?.id) {
      return
    }

    const items = allowedValues
    const reorderedOption = items.find(item => item === dragMetadata.id)

    if (!reorderedOption) return

    const newlyOrderedItems = items.reduce<string[]>((newItems, item) => {
      if (item === reorderedOption) {
        return newItems
      }

      if (item !== dropMetadata?.id) {
        newItems.push(item)
      } else if (isBefore) {
        newItems.push(reorderedOption, item)
      } else if (!isBefore) {
        newItems.push(item, reorderedOption)
      }

      return newItems
    }, [])

    onAllowedValuesChange({type: 'reorder', values: newlyOrderedItems})
  }

  function removeAllowedValue(index: number) {
    onAllowedValuesChange({type: 'remove', values: allowedValues.filter((_, i) => i !== index)})

    const focusElement = getFocusListItem(allowedValuesItemsRefs.current, index) || newAllowedValueRef
    allowedValuesItemsRefs.current.splice(index, 1)

    // ActionList needs to unmount to release the focus trap. setTimeout puts focus in the next rendering cycle.
    setTimeout(() => focusElement.current?.focus())
  }

  return (
    <div className={clsx(styles.optionsContainer, 'pr-mb-3')}>
      <FormControl className={styles.optionsFormControl}>
        <FormControl.Label htmlFor={NEW_ALLOWED_VALUE_ELEMENT_ID} visuallyHidden>
          Options
        </FormControl.Label>
        <div className={styles.optionsInputGroup}>
          <TextInput
            block
            id={NEW_ALLOWED_VALUE_ELEMENT_ID}
            placeholder="Add option..."
            ref={newAllowedValueRef}
            aria-invalid={!!newAllowedValueError}
            aria-describedby={newAllowedValueError ? 'property-new-allowed-value-validation' : undefined}
            value={newAllowedValue}
            onChange={e => onNewAllowedValueChange(e.target.value)}
            onKeyDown={e =>
              // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
              e.key === 'Enter' && onAllowedValuesChange({type: 'add', values: [newAllowedValue].concat(allowedValues)})
            }
          />

          <Button onClick={() => onAllowedValuesChange({type: 'add', values: [newAllowedValue].concat(allowedValues)})}>
            Add
          </Button>
        </div>
        {newAllowedValueError && (
          <FormControl.Validation id="property-new-allowed-value-validation" variant="error">
            {newAllowedValueError}
          </FormControl.Validation>
        )}
      </FormControl>

      {allowedValues.length > 0 ? (
        <DragAndDrop
          items={allowedValues.map(value => {
            return {id: value, title: value}
          })}
          onDrop={onDrop}
          style={{listStyleType: 'none', maxHeight: '500px', overflowY: 'auto'}}
          renderOverlay={({id: value}, index) => (
            <DragAndDrop.Item
              aria-label={value}
              index={index}
              id={value}
              title={value}
              key={value}
              hideTrigger
              style={{borderTop: 'solid 1px var(--borderColor-default, var(--color-border-default))'}}
              isDragOverlay
            >
              <AllowedValueItem
                value={value}
                isDefaultValue={defaultValues.includes(value)}
                onDelete={() => removeAllowedValue(index)}
                index={index}
              />
            </DragAndDrop.Item>
          )}
        >
          {allowedValues.map((value, index) => (
            <DragAndDrop.Item
              aria-label={value}
              index={index}
              id={value}
              title={value}
              key={value}
              hideTrigger
              style={{borderTop: 'solid 1px var(--borderColor-default, var(--color-border-default))'}}
            >
              <AllowedValueItem
                value={value}
                isDefaultValue={defaultValues.includes(value)}
                onDelete={() => removeAllowedValue(index)}
                index={index}
                setMenuAnchorRef={ref => (allowedValuesItemsRefs.current[index] = ref)}
              />
            </DragAndDrop.Item>
          ))}
        </DragAndDrop>
      ) : (
        <div className={styles.emptyOptionsMessage}>No options</div>
      )}
    </div>
  )
}

function AllowedValueItem({
  value,
  isDefaultValue,
  onDelete,
  index,
  setMenuAnchorRef,
}: {
  value: string
  isDefaultValue: boolean
  onDelete(): void
  index: number
  setMenuAnchorRef?: (ref: React.RefObject<HTMLButtonElement | null>) => void
}) {
  const {openMoveDialog} = useDragAndDrop()
  const editButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    setMenuAnchorRef?.(editButtonRef)
  }, [setMenuAnchorRef])

  return (
    <div className={styles.allowedValueItem}>
      <div className={styles.allowedValueContent}>
        <DragAndDrop.DragTrigger style={{marginRight: 0}} />
        {value}
        {isDefaultValue && <Label variant="accent">Default</Label>}
      </div>
      <div>
        <ActionMenu anchorRef={editButtonRef}>
          <ActionMenu.Anchor>
            <IconButton
              icon={KebabHorizontalIcon}
              variant="invisible"
              aria-label={`More options for ${value}`}
              description="More options"
            />
          </ActionMenu.Anchor>

          <ActionMenu.Overlay>
            <ActionList>
              <ActionList.Item
                onSelect={() => openMoveDialog(value, index, editButtonRef)}
                aria-label={`Advanced move ${value}...`}
              >
                <ActionList.LeadingVisual>
                  <GrabberIcon />
                </ActionList.LeadingVisual>
                Advanced Move...
              </ActionList.Item>
              <ActionList.Item variant="danger" onSelect={onDelete} aria-label={`Delete option ${value}`}>
                Delete
              </ActionList.Item>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </div>
    </div>
  )
}

function getFocusListItem<T>(items: T[], index: number): T | null {
  const prevEl = items[index - 1]
  const nextEl = items[index + 1]

  return nextEl || prevEl || null
}
